import { useEffect, useMemo, useState } from 'react';
import { Button, Card, CardContent, Skeleton, Tooltip, Typography } from '@mui/material';
import { GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid';
import InfoIcon from '@mui/icons-material/Info';
import { Box } from '@mui/system';
import { formatarDocumento } from '../../../../components/Utils/documentoAlfanumerico';
import {
  atualizarSelecaoTitulos,
  getDadosConsolidadosCedente,
  getDadosConsolidadosSacado,
  getTitulosResumoSelecao
} from '../../../../api/capitalFlow';

const useTabsSimulacaoHook = ({ buscarSimulacaoResumo, buscarAtualizarSimulacao }) => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingGrid, setLoadingGrid] = useState(true);
  const [rows, setRows] = useState([]);
  const [contadorFiltros, setContadorFiltros] = useState(0);
  const [firstLoadGrid, setFirstLoadGrid] = useState(false);
  const [presetFiltros, setPresetFiltros] = useState(null);
  const [dadosSacados, setDadosSacados] = useState([]);
  const [dadosCedentes, setDadosCedentes] = useState([]);
  const [controleBuscarDados, setControleBuscarDados] = useState({});
  const [selectedRowsMap, setSelectedRowsMap] = useState({});
  const [sortModel, setSortModel] = useState([]);
  const [filtrosAtuais, setFiltrosAtuais] = useState({});
  const [loadingSelectionMap, setLoadingSelectionMap] = useState({});
  const [rowCount, setRowCount] = useState(0);
  const tabs = [
    { label: 'Por sacado', key: 'porSacado' },
    { label: 'Por cedente', key: 'porCedente' },
    { label: 'Por título', key: 'porTitulo' }
  ];
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });

  const currentList = useMemo(() => {
    switch (tabs[tab].key) {
      case 'porSacado':
        return dadosSacados;
      case 'porCedente':
        return dadosCedentes;
      default:
        return [];
    }
  }, [tab, dadosSacados, dadosCedentes]);

  const buscaDadosSacados = () => {
    if (controleBuscarDados?.sacado) {
      return;
    }
    setLoading(true);
    return getDadosConsolidadosSacado()
      .then(response => {
        const lista = (response.data ?? []).map((item, i) => ({
          id: `sacado-${i}`,
          nome: item.NOMESACADO,
          cpfCnpj: item.CPFCNPJSACADO,
          limiteParaOperar: item.LIMITESACADO,
          limiteBruto: item.LIMITEBRUTOSACADO,
          riscoAtual: item.RISCOATUALSACADO,
          valorTotal: item.VALORTOTALTITULOS,
          nTitulos: item.QUANTIDADETITULOS,
          nCedentesDiferentes: item.QUANTIDADECEDENTES,
          riscoParcial: item.MENSAGEM !== null,
          mensagemRisco: item.MENSAGEM,
          tipo: 'sacado'
        }));
        setDadosSacados(lista);
        setControleBuscarDados({ ...controleBuscarDados, sacado: true });
      })
      .finally(() => setLoading(false));
  };

  const buscaDadosCedentes = () => {
    if (controleBuscarDados?.cedente) {
      return;
    }

    setLoading(true);
    return getDadosConsolidadosCedente()
      .then(response => {
        const lista = (response.data ?? []).map((item, i) => ({
          id: `sacado-${i}`,
          nome: item.NOMECEDENTE,
          cpfCnpj: item.CPFCNPJCEDENTE,
          limiteParaOperar: item.LIMITECEDENTE,
          limiteBruto: item.LIMITEBRUTOCEDENTE,
          riscoAtual: item.RISCOATUALCEDENTE,
          valorTotal: item.VALORTOTALTITULOS,
          nTitulos: item.QUANTIDADETITULOS,
          nSacadosDiferentes: item.QUANTIDADESACADOS,
          riscoParcial: item.MENSAGEM !== null,
          mensagemRisco: item.MENSAGEM,
          tipo: 'cedente'
        }));
        setDadosCedentes(lista);
        setControleBuscarDados({ ...controleBuscarDados, cedente: true });
      })
      .finally(() => setLoading(false));
  };

  const handleAction = item => {
    const filtros = {};
    setFirstLoadGrid(true);

    if (item.tipo === 'sacado') {
      filtros.cpfcnpj_sacado = item.cpfCnpj;
    } else if (item.tipo === 'cedente') {
      filtros.cpfcnpj_cedente = item.cpfCnpj;
    }

    filtros.filtro_origem = 'abas';
    setPresetFiltros(filtros);

    const porTituloIndex = tabs.findIndex(t => t.key === 'porTitulo');
    if (porTituloIndex !== -1) {
      setTab(porTituloIndex);
    }

    handleFiltrar(filtros);
  };

  const brCurrency = v => {
    const valor = Number(v);
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
      Number.isFinite(valor) ? valor : 0
    );
  };

  const columns = useMemo(() => [
    {
      field: 'NOMECEDENTE',
      headerName: 'Cedente',
      flex: 1,
      minWidth: 120,
      valueGetter: ({ row }) => `${formatarDocumento(row.CPFCNPJCEDENTE)} - ${row.NOMECEDENTE}`
    },
    {
      field: 'NOMESACADO',
      headerName: 'Sacado',
      flex: 1,
      minWidth: 120,
      valueGetter: ({ row }) => `${formatarDocumento(row.CPFCNPJSACADO)} - ${row.NOMESACADO}`
    },
    { field: 'NF', headerName: 'NF', flex: 0.8, minWidth: 70 },
    { field: 'NDOC', headerName: 'Nº Doc.', flex: 0.8, minWidth: 100 },
    { field: 'TTO', headerName: 'Tipo Op.', flex: 0.8, minWidth: 70 },
    {
      field: 'VENCIMENTO',
      headerName: 'Vencimento',
      flex: 1.0,
      minWidth: 100,
      valueFormatter: ({ value }) => fmtBRDate(value)
    },
    {
      field: 'VALOR',
      headerName: 'Valor (R$)',
      flex: 1.0,
      minWidth: 110,
      valueFormatter: ({ value }) => formataValor(value)
    }
  ]);

  const formataValor = valor =>
    parseFloat(valor).toLocaleString('pt-br', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

  const cleanFilters = (obj = {}) =>
    Object.fromEntries(Object.entries(obj).filter(([_, v]) => !isBlank(v)));

  const fmtBRDate = iso => {
    const d = parseISODateLocal(iso);
    return d ? d.toLocaleDateString('pt-BR') : '';
  };

  const isBlank = v =>
    v === null ||
    v === undefined ||
    (typeof v === 'string' && v.trim() === '') ||
    (Array.isArray(v) && v.length === 0) ||
    (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

  const parseISODateLocal = s => {
    if (!s) return null;
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const d = new Date(+m[1], +m[2] - 1, +m[3], 0, 0, 0, 0);
      return isNaN(d) ? null : d;
    }
    const d = new Date(s);
    return isNaN(d) ? null : d;
  };

  const verificaContaFiltros = (filtros = {}) => {
    const on = v => (typeof v === 'string' ? v.trim() !== '' : !!v);
    let n = 0;
    if (on(filtros.nome_cedente)) n++;
    if (on(filtros.cpfcnpj_cedente)) n++;
    if (on(filtros.nome_sacado)) n++;
    if (on(filtros.cpfcnpj_sacado)) n++;
    if (on(filtros.tipo_operacao)) n++;
    if (on(filtros.tipo_documento)) n++;
    if (on(filtros.numero_documento)) n++;
    if (on(filtros.bordero)) n++;
    if (filtros.data_aceite_inicio || filtros.data_aceite_fim) n++;
    if (filtros.data_venc_inicio || filtros.data_venc_fim) n++;
    return n;
  };

  const handleFiltrar = (filtros = {}) => {
    const filtrosLimpos = cleanFilters(filtros);
    const newModel = {
      ...paginationModel,
      page: 0
    };

    setPaginationModel(newModel);
    setContadorFiltros(verificaContaFiltros(filtros));
    carregarTitulos(newModel, filtrosLimpos);
  };

  const handleRowSelectionModelChange = async newRowSelectionModel => {
    setLoadingSelectionMap(prev => {
      const updated = { ...prev };
      rows.forEach(r => {
        updated[String(r.id)] = true;
      });
      return updated;
    });

    const consistentIds = newRowSelectionModel.map(String);
    const currentPageRowIds = rows.map(row => String(row.id));
    const newSelectedRowsMap = { ...selectedRowsMap };
    const allRowsSelected = consistentIds.length === rows.length;
    const noneSelected = consistentIds.length === 0;

    if (allRowsSelected) {
      const allRows = rows.map(r => ({ uuid_titulos: r.id, checked: true }));
      await apiSelecionarTitulo(allRows);
      rows.forEach(r => {
        newSelectedRowsMap[String(r.id)] = r;
      });
      setSelectedRowsMap(newSelectedRowsMap);
      setLoadingSelectionMap({});
      buscarAtualizarSimulacao();
      setControleBuscarDados({});
      return;
    }

    if (noneSelected) {
      const allRows = rows.map(r => ({ uuid_titulos: r.id, checked: false }));
      await apiSelecionarTitulo(allRows);
      currentPageRowIds.forEach(id => delete newSelectedRowsMap[id]);
      setSelectedRowsMap(newSelectedRowsMap);
      setLoadingSelectionMap({});
      buscarAtualizarSimulacao();
      setControleBuscarDados({});
      return;
    }

    const promises = [];

    for (const rowId of currentPageRowIds) {
      const currentRow = rows.find(row => String(row.id) === rowId);
      const isRowSelected = consistentIds.includes(rowId);

      if (isRowSelected) {
        if (!newSelectedRowsMap[rowId]) {
          promises.push(apiSelecionarTitulo({ uuid_titulos: currentRow.id, checked: true }));
        }
        newSelectedRowsMap[rowId] = currentRow;
      } else {
        if (newSelectedRowsMap[rowId]) {
          promises.push(apiSelecionarTitulo({ uuid_titulos: currentRow.id, checked: false }));
        }
        delete newSelectedRowsMap[rowId];
      }
    }

    await Promise.all(promises);
    setSelectedRowsMap(newSelectedRowsMap);
    setLoadingSelectionMap({});
    buscarAtualizarSimulacao();
    setControleBuscarDados({});
  };

  const apiSelecionarTitulo = payload => {
    const items = Array.isArray(payload) ? payload : [payload];
    const uuid_titulos = items.map(i => i.uuid_titulos);
    const { checked } = items[0];

    const params = {
      uuid_titulos,
      checked
    };

    return atualizarSelecaoTitulos(params);
  };

  useEffect(() => {
    if (buscarSimulacaoResumo > 0) {
      const { key } = tabs[tab];
      if (key === 'porSacado') {
        buscaDadosSacados();
      } else if (key === 'porCedente') {
        buscaDadosCedentes();
      } else if (key === 'porTitulo' && !firstLoadGrid) {
        setFirstLoadGrid(true);
        const filtros = {};
        filtros.filtro_origem = 'abas';
        setPresetFiltros(filtros);
        handleFiltrar(filtros);
      }
    }
  }, [tab, buscarSimulacaoResumo]);

  function SugestaoCard({ item, onAction }) {
    return (
      <Card className="simu-card" elevation={0}>
        <CardContent className="simu-card__content">
          <Typography className="simu-card__top" variant="caption">
            {formatarDocumento(item.cpfCnpj)}
          </Typography>

          <Typography className="simu-card__title" variant="subtitle1">
            <Tooltip title={item.nome}>
              <span>{item.nome}</span>
            </Tooltip>
          </Typography>

          <Box className="simu-card__block">
            {(item.tipo === 'cedente' || item.tipo === 'sacado') && (
              <>
                <Typography className="simu-card__label">Limite bruto</Typography>
                <Typography className="simu-card__value">{brCurrency(item.limiteBruto ?? 0)}</Typography>
              </>
            )}
          </Box>

          <Box className="simu-card__block">
            {(item.tipo === 'cedente' || item.tipo === 'sacado') && (
              <>
                <Typography className="simu-card__label">Risco atual</Typography>
                <Typography className="simu-card__value">{brCurrency(item.riscoAtual ?? 0)}</Typography>
              </>
            )}
          </Box>

          <Box className="simu-card__block">
            <Typography className="simu-card__label">Limite para operar</Typography>
            <Typography className="simu-card__value">{brCurrency(item.limiteParaOperar)}</Typography>
          </Box>

          <Box className="simu-card__block">
            <Typography className="simu-card__label">Nº Títulos</Typography>
            <Typography className="simu-card__value">{item.nTitulos}</Typography>
          </Box>

          <Box className="simu-card__block">
            {item.tipo === 'cedente' ? (
              <>
                <Typography className="simu-card__label">Nº Sacados diferentes</Typography>
                <Typography className="simu-card__value">{item.nSacadosDiferentes}</Typography>
              </>
            ) : (
              <>
                <Typography className="simu-card__label">Nº Cedentes diferentes</Typography>
                <Typography className="simu-card__value">{item.nCedentesDiferentes}</Typography>
              </>
            )}
          </Box>

          <Box className="simu-card__block">
            <Typography className="simu-card__label">Valor total</Typography>
            <Box className="simu-card__value-row">
              <Tooltip
                arrow
                title={
                  item.riscoParcial
                    ? 'Ao pedir esse valor é possível que sua operação seja aceita parcialmente'
                    : ''
                }
              >
                <span>
                  <Typography
                    className={`simu-card__value d-flex ${item.riscoParcial ? 'is-warning' : ''}`}
                  >
                    {brCurrency(item.valorTotal)}
                    {item.riscoParcial && <InfoIcon className="icon-warning is-warning" />}
                  </Typography>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Box className="simu-card__footer">
            <Button variant="contained" className="simu-card__cta" onClick={() => onAction?.(item)}>
              Ver títulos selecionados
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  function SugestaoCardSkeleton() {
    return (
      <Card className="simu-card simu-card__skeleton" elevation={0}>
        <CardContent className="simu-card__content">
          <Box className="mt-2 mb-1">
            <Skeleton
              variant="text"
              className="skeleton-custom"
              animation="wave"
              width="95%"
              height={50}
            />
          </Box>

          <Box className="simu-card__block">
            <Skeleton variant="text" width="85%" height={50} />
          </Box>

          <Box className="simu-card__block">
            <Skeleton variant="text" width="85%" height={50} />
          </Box>

          <Box className="simu-card__block">
            <Skeleton variant="text" width="85%" height={50} />
          </Box>

          <Box className="simu-card__block">
            <Skeleton variant="text" width="85%" height={50} />
          </Box>

          <Box className="simu-card__footer">
            <Skeleton variant="rectangular" width="100%" height={36} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const toolbarTabela = () => (
    <GridToolbarContainer className="tabela-toolbar">
      <div className="toolbar-spacer" />
      <GridToolbarQuickFilter className="search-grid" placeholder="Procurar..." debounceMs={500} />
    </GridToolbarContainer>
  );

  const carregarTitulos = (model = paginationModel, novosFiltros = null, newSortModel = sortModel) => {
    const filtrosBase = novosFiltros ?? filtrosAtuais;
    const filtrosLimpos = cleanFilters(filtrosBase);
    const listaAtual = rows;
    const sort = newSortModel?.[0];
    setRows([]);
    setFiltrosAtuais(filtrosLimpos);
    filtrosLimpos.filtro_origem = filtrosLimpos.filtro_origem ?? 'geral';
    const { page, pageSize } = model;
    const params = {
      ...filtrosLimpos,
      page: page,
      per_page: pageSize,
      order_by: sort?.sort ?? null,
      column_filter: sort?.field ?? null
    };

    setLoadingGrid(true);
    getTitulosResumoSelecao(params)
      .then(response => {
        const { data } = response.data;
        setRows(data ?? []);

        const newSelected = {};
        (data ?? []).forEach(row => {
          if (row.SELECIONADO === 'S') {
            newSelected[String(row.id)] = row;
          }
        });
        setSelectedRowsMap(prev => ({ ...prev, ...newSelected }));

        setRowCount(response.data.meta.total);
      })
      .catch(error => {
        setRows(listaAtual);
      })
      .finally(() => setLoadingGrid(false));
  };

  const rowSelectionModelFiltered = rows
    .map(row => String(row.id))
    .filter(id => selectedRowsMap[id] !== undefined);

  const handlePaginationModelChange = model => {
    setPaginationModel(model);
    carregarTitulos(model, filtrosAtuais, sortModel);
  };

  const handleSortModelChange = model => {
    setSortModel(model);
    carregarTitulos(paginationModel, filtrosAtuais, model);
  };

  return {
    dadosCedentes,
    dadosSacados,
    tab,
    setTab,
    tabs,
    currentList,
    handleAction,
    SugestaoCard,
    handleFiltrar,
    toolbarTabela,
    contadorFiltros,
    SugestaoCardSkeleton,
    loading,
    loadingGrid,
    columns,
    rows,
    presetFiltros,
    handleRowSelectionModelChange,
    rowSelectionModelFiltered,
    paginationModel,
    handlePaginationModelChange,
    sortModel,
    handleSortModelChange,
    rowCount,
    loadingSelectionMap
  };
};

export default useTabsSimulacaoHook;
